import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPut } from "../api";
import type { Dayjs } from "dayjs";

export interface VoucherType {
  id: number;
  code: string;
  name: string;
  default_rides: number;
  ride_length_min: number;
  price_czk: number;
  validity_months: number;
  active: boolean;
  low_stock_threshold: number;
  barcode_type_digits: string | null;
  available_count: number;
  created_at: Dayjs;
  updated_at: Dayjs;
}

interface VoucherTypeListResponse {
  voucher_types: VoucherType[];
}

export interface CreateVoucherTypePayload {
  code: string;
  name: string;
  default_rides: number;
  ride_length_min: number;
  price_czk: number;
  validity_months: number;
  active: boolean;
  low_stock_threshold: number;
  barcode_type_digits?: string | null;
}

export type UpdateVoucherTypePayload = Partial<Omit<CreateVoucherTypePayload, "code">>;

export function useVoucherTypes() {
  return useQuery<VoucherType[]>({
    queryKey: ["voucher-types"],
    queryFn: async () => {
      const res = await apiGet<VoucherTypeListResponse>("/voucher-types");
      return res.voucher_types;
    },
  });
}

export function useCreateVoucherType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateVoucherTypePayload) =>
      apiPost<VoucherType>("/voucher-types", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["voucher-types"] }),
  });
}

export function useUpdateVoucherType(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateVoucherTypePayload) =>
      apiPut<VoucherType>(`/voucher-types/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["voucher-types"] }),
  });
}
